import { useEffect, useId, useMemo, useRef, useState } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
  aliases?: readonly string[];
  /** A second line in the list, e.g. a shipment's recipient and date; also searched. */
  detail?: string;
  /** A result count that gives a filter option useful operational context. */
  count?: number;
  /** Adds a unit to the compact count for assistive technology and hover context. */
  countLabel?: string;
}

interface SearchableSelectProps {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly SearchableSelectOption[];
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

function searchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

function countText(option: SearchableSelectOption): string | undefined {
  if (option.count === undefined) return undefined;
  const value = option.count.toLocaleString();
  return option.countLabel ? `${value} ${option.countLabel}` : value;
}

export function SearchableSelect({
  ariaLabel,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  disabled = false,
  autoFocus = false,
  className,
}: SearchableSelectProps) {
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryRef = useRef('');
  const hasTypedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hasTyped, setHasTyped] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = options.find((option) => option.value === value);
  const selectedCount = selected ? countText(selected) : undefined;
  const normalizedQuery = searchText(query.trim());
  const visibleOptions = useMemo(
    () =>
      normalizedQuery
        ? options.filter((option) =>
            [option.label, option.value, option.detail ?? '', ...(option.aliases ?? [])]
              .map(searchText)
              .some((candidate) => candidate.includes(normalizedQuery)),
          )
        : [...options],
    [normalizedQuery, options],
  );
  const inputValue = open && hasTyped ? query : (selected?.label ?? '');

  useEffect(() => {
    function onPointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onFocusIn(event: FocusEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [normalizedQuery, open]);

  function selectOption(nextValue: string): void {
    onChange(nextValue);
    setOpen(false);
    queryRef.current = '';
    hasTypedRef.current = false;
    setQuery('');
    setHasTyped(false);
  }

  function openPicker(selectText = false): void {
    if (disabled) return;
    setOpen(true);
    queryRef.current = '';
    hasTypedRef.current = false;
    setHasTyped(false);
    setQuery('');
    if (selectText) {
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }

  function startSearch(queryText = ''): void {
    setOpen(true);
    queryRef.current = queryText;
    hasTypedRef.current = true;
    setHasTyped(true);
    setQuery(queryText);
    setActiveIndex(0);
  }

  return (
    <div
      className={`searchable-select${selectedCount ? ' searchable-select-has-count' : ''}${open && hasTyped ? ' searchable-select-querying' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
    >
      <input
        aria-activedescendant={
          open && visibleOptions[activeIndex]
            ? `${menuId}-${visibleOptions[activeIndex].value}`
            : undefined
        }
        aria-autocomplete="list"
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-description={selectedCount ? `Matching ${selectedCount}` : undefined}
        autoComplete="off"
        autoFocus={autoFocus}
        className="searchable-select-input"
        disabled={disabled}
        ref={inputRef}
        onChange={(event) => {
          startSearch(event.target.value);
        }}
        onFocus={() => {
          requestAnimationFrame(() => inputRef.current?.select());
        }}
        onMouseDown={(event) => {
          if (disabled || event.button !== 0) return;
          // Prevent native mouse placement from moving the caret into the selected label.
          // A searchable picker should always begin a fresh query when the user starts typing.
          event.preventDefault();
          inputRef.current?.focus({ preventScroll: true });
          openPicker(true);
        }}
        onPaste={(event) => {
          if (hasTyped) return;
          event.preventDefault();
          startSearch(event.clipboardData.getData('text'));
        }}
        onKeyDown={(event) => {
          const isTyping =
            event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;
          if (isTyping) {
            event.preventDefault();
            startSearch(`${hasTypedRef.current ? queryRef.current : ''}${event.key}`);
            return;
          }

          if (event.key === 'Backspace') {
            event.preventDefault();
            startSearch(hasTypedRef.current ? queryRef.current.slice(0, -1) : '');
            return;
          }

          if (event.key === 'ArrowDown') {
            event.preventDefault();
            openPicker();
            setActiveIndex((index) => Math.min(index + 1, Math.max(visibleOptions.length - 1, 0)));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            openPicker();
            setActiveIndex((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter' && open && visibleOptions[activeIndex]) {
            event.preventDefault();
            selectOption(visibleOptions[activeIndex].value);
          } else if (event.key === 'Escape') {
            setOpen(false);
            queryRef.current = '';
            hasTypedRef.current = false;
            setHasTyped(false);
          }
        }}
        placeholder={placeholder}
        required={required && !value}
        role="combobox"
        value={inputValue}
      />
      {selectedCount ? (
        <span className="searchable-select-count" aria-hidden="true">
          {selected?.count?.toLocaleString()}
        </span>
      ) : null}
      <span className="searchable-select-chevron" aria-hidden="true" />
      {open ? (
        <div className="searchable-select-menu" id={menuId} role="listbox">
          {visibleOptions.length > 0 ? (
            visibleOptions.map((option, index) => (
              <div
                aria-label={option.label}
                aria-description={
                  [option.detail, countText(option)].filter(Boolean).join(', ') || undefined
                }
                aria-selected={option.value === value}
                className={`searchable-select-option${index === activeIndex ? ' is-active' : ''}`}
                data-count={option.count === undefined ? undefined : option.count.toLocaleString()}
                id={`${menuId}-${option.value}`}
                key={option.value}
                onMouseDown={(event) => {
                  // Keep focus on the query field until the click completes. Closing the list on
                  // mousedown detaches this option before pointer users can finish selecting it.
                  event.preventDefault();
                }}
                onClick={() => {
                  selectOption(option.value);
                }}
                role="option"
              >
                {option.detail ? (
                  <span className="searchable-select-option-text">
                    <span className="searchable-select-option-label">{option.label}</span>
                    <span className="searchable-select-option-detail">{option.detail}</span>
                  </span>
                ) : (
                  <span className="searchable-select-option-label">{option.label}</span>
                )}
              </div>
            ))
          ) : (
            <p className="searchable-select-empty">No matches</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
